import {
  TokenDistributionManager,
  TokenDistributionManager__factory,
} from "@gu-corp/joc-token-manager-contracts";
import { useProvider, useSigner, useStatus } from "@gu-corp/gu-wallet-connector";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { env } from "../libraries/utils/env";
import useIsInTheWrongNetwork from "../hooks/useIsInTheWrongNetwork";

export interface ContractContextType {
  contract: TokenDistributionManager | null;
  roles: {
    ADMINISTRATOR_ROLE: string;
    KEY_KEEPER_ROLE: string;
    MANAGER_ROLE: string;
    OPERATOR_ROLE: string;
    OWNER_ROLE: string;
  };
  version: string;
  metadata: {
    symbol: string;
    decimals: number;
    name: string;
  };
  maximumPeriod: number;
  refetchMaximumPeriod: () => Promise<void>;
  maximumExtendPeriod: number;
  refetchMaximumExtendPeriod: () => Promise<void>;
  balances: {
    totalSupply: bigint;
    totalLocked: bigint;
    totalProvisional: bigint;
  };
  refetchBalances: () => Promise<void>;
}

export const ContractContext = createContext<ContractContextType>({
  contract: null,
  roles: {
    ADMINISTRATOR_ROLE: "",
    KEY_KEEPER_ROLE: "",
    MANAGER_ROLE: "",
    OPERATOR_ROLE: "",
    OWNER_ROLE: "",
  },
  version: "",
  metadata: {
    symbol: "",
    decimals: 18,
    name: "",
  },
  maximumPeriod: 0,
  refetchMaximumPeriod: async () => {},
  maximumExtendPeriod: 0,
  refetchMaximumExtendPeriod: async () => {},
  balances: {
    totalSupply: BigInt(0),
    totalLocked: BigInt(0),
    totalProvisional: BigInt(0),
  },
  refetchBalances: async () => {},
});

export const ContractProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const status = useStatus();
  const isInTheWrongNetwork = useIsInTheWrongNetwork();
  const isConnected = status === "connected";
  const provider = useProvider();
  const signer = useSigner();
  const contract = useMemo(() => {
    if (!isConnected || isInTheWrongNetwork) {
      return null;
    }
    return TokenDistributionManager__factory.connect(
      env.REACT_APP_CONTRACT_ADDRESS?.toLowerCase() as string,
      signer || provider
    );
  }, [signer, provider, isConnected, isInTheWrongNetwork]);
  const [roles, setRoles] = useState({
    ADMINISTRATOR_ROLE: "",
    KEY_KEEPER_ROLE: "",
    MANAGER_ROLE: "",
    OPERATOR_ROLE: "",
    OWNER_ROLE: "",
  });
  const [version, setVersion] = useState("");
  const [metadata, setMetadata] = useState({
    symbol: "",
    decimals: 18,
    name: "",
  });
  const [maximumPeriod, setMaximumPeriod] = useState(0);
  const refetchMaximumPeriod = useCallback(async () => {
    if (!contract) return;
    const maximumPeriod = await contract.maximumLockupPeriod();
    setMaximumPeriod(Number(maximumPeriod));
  }, [contract]);
  const [maximumExtendPeriod, setMaximumExtendPeriod] = useState(0);
  const refetchMaximumExtendPeriod = useCallback(async () => {
    if (!contract) return;
    const maximumExtendPeriod = await contract.maximumLockupExtendPeriod();
    setMaximumExtendPeriod(Number(maximumExtendPeriod));
  }, [contract]);
  const [balances, setBalances] = useState({
    totalSupply: BigInt(0),
    totalLocked: BigInt(0),
    totalProvisional: BigInt(0),
  });
  const refetchBalances = useCallback(async () => {
    if (!contract) return;
    const totalSupply = await contract.totalSupply();
    const totalLocked = await contract.totalLocked();
    const totalProvisional = await contract.totalProvisional();

    setBalances({
      totalSupply: BigInt(totalSupply),
      totalLocked: BigInt(totalLocked),
      totalProvisional: BigInt(totalProvisional),
    });
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const totalSupply = await contract.totalSupply();
      const totalLocked = await contract.totalLocked();
      const totalProvisional = await contract.totalProvisional();

      setBalances({
        totalSupply: BigInt(totalSupply),
        totalLocked: BigInt(totalLocked),
        totalProvisional: BigInt(totalProvisional),
      });
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const maximumExtendPeriod = await contract.maximumLockupExtendPeriod();
      setMaximumExtendPeriod(Number(maximumExtendPeriod));
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const maximumPeriod = await contract.maximumLockupPeriod();
      setMaximumPeriod(Number(maximumPeriod));
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const symbol = await contract.symbol();
      const name = await contract.name();
      const decimals = await contract.decimals();
      setMetadata({
        name,
        symbol,
        decimals: +decimals.toString(),
      });
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const version = await contract.VERSION();
      setVersion(version);
    })();
  }, [contract]);

  useEffect(() => {
    (async () => {
      if (!contract) return;
      const ADMINISTRATOR_ROLE = await contract.ADMINISTRATOR_ROLE();
      const KEY_KEEPER_ROLE = await contract.KEY_KEEPER_ROLE();
      const MANAGER_ROLE = await contract.MANAGER_ROLE();
      const OPERATOR_ROLE = await contract.OPERATOR_ROLE();
      const OWNER_ROLE = await contract.OWNER_ROLE();

      setRoles({
        ADMINISTRATOR_ROLE,
        KEY_KEEPER_ROLE,
        MANAGER_ROLE,
        OPERATOR_ROLE,
        OWNER_ROLE,
      });
    })();
  }, [contract]);

  return (
    <ContractContext.Provider
      value={{
        contract,
        roles,
        version,
        metadata,
        maximumPeriod,
        refetchMaximumPeriod,
        maximumExtendPeriod,
        refetchMaximumExtendPeriod,
        balances,
        refetchBalances,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};

export const useContract = (): TokenDistributionManager | null => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useContract must be used within a ContractProvider");
  }
  return context.contract;
};

export const useRoles = (): {
  ADMINISTRATOR_ROLE: string;
  KEY_KEEPER_ROLE: string;
  MANAGER_ROLE: string;
  OPERATOR_ROLE: string;
  OWNER_ROLE: string;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useRoles must be used within a ContractProvider");
  }
  return context.roles;
};

export const useVersion = (): string => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useVersion must be used within a ContractProvider");
  }
  return context.version;
};

export const useMetadata = (): {
  symbol: string;
  decimals: number;
  name: string;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useMetadata must be used within a ContractProvider");
  }
  return context.metadata;
};

export const useMaximumPeriod = (): {
  data: number;
  refetch: () => Promise<void>;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useMaximumPeriod must be used within a ContractProvider");
  }
  return {
    data: context.maximumPeriod,
    refetch: context.refetchMaximumPeriod,
  };
};

export const useMaximumExtendPeriod = (): {
  data: number;
  refetch: () => Promise<void>;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error(
      "useMaximumExtendPeriod must be used within a ContractProvider"
    );
  }
  return {
    data: context.maximumExtendPeriod,
    refetch: context.refetchMaximumExtendPeriod,
  };
};

export const useBalances = (): {
  data: {
    totalSupply: bigint;
    totalLocked: bigint;
    totalProvisional: bigint;
  };
  refetch: () => Promise<void>;
} => {
  const context = useContext(ContractContext);
  if (!context) {
    throw new Error("useBalances must be used within a ContractProvider");
  }
  return {
    data: context.balances,
    refetch: context.refetchBalances,
  };
};
